import type { OrderItem } from "@models/order";

export function coerceProductId(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "string") {
    const t = value.trim();
    return t.length > 0 ? t : undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
}

const MY_ORDERS_LIST_KEYS = [
  "items",
  "orders",
  "data",
  "order_items",
  "my_orders",
  "list",
  "response",
  "order_history",
  "purchases",
  "lines",
  "rows",
  "results",
  "values",
  "entries",
  "history",
  "listing",
  "records",
  "transactions",
  "order_list",
] as const;

/** After axios interceptor, payload may be an array or an object (possibly nested) wrapping the list */
export function normalizeMyOrdersPayload(data: unknown): OrderItem[] {
  if (Array.isArray(data)) {
    return data as OrderItem[];
  }
  if (!data || typeof data !== "object") {
    return [];
  }
  const o = data as Record<string, unknown>;
  for (const key of MY_ORDERS_LIST_KEYS) {
    const v = o[key];
    if (Array.isArray(v)) {
      return v as OrderItem[];
    }
  }
  for (const key of MY_ORDERS_LIST_KEYS) {
    const v = o[key];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const inner = normalizeMyOrdersPayload(v);
      if (inner.length > 0) {
        return inner;
      }
    }
  }
  return [];
}

/**
 * Reads catalog product id from API row using explicit product-related fields only
 * (merged onto `product_id` in getOrders).
 */
export function resolveProductIdFromOrderRow(
  row: Record<string, unknown>
): string | undefined {
  for (const k of [
    "product_id",
    "productId",
    "store_product_id",
    "catalog_product_id",
    "pay_offer_product_id",
    "offer_product_id",
    "product_uuid",
    "content_id",
    "game_product_id",
  ]) {
    const id = coerceProductId(row[k]);
    if (id) return id;
  }

  for (const nk of [
    "product",
    "item",
    "product_info",
    "catalog_product",
    "offer",
    "sku",
    "line_item",
    "line",
  ]) {
    const nested = row[nk];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const p = nested as Record<string, unknown>;
      for (const k of ["product_id", "product_uuid", "content_id"]) {
        const id = coerceProductId(p[k]);
        if (id) return id;
      }
    }
  }

  return undefined;
}
